
import {
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  isToday,
  isSameMonth,
  parse,
  startOfToday,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  eachWeekOfInterval,
} from 'date-fns';

import { useState } from 'react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover.tsx';
import { CalendarEvent } from '../../types';

type DashCalendarProps = {
  frequencies: { date: string, frequency: number }[],
  events: CalendarEvent[]
}

const theme = () => {
  const url = window.location.href
  if (url.includes("chapel")) {
    return {
      r: 56,
      g: 106,
      b: 255
    }
  }
  if (url.includes("deko")) {
    return {
      r: 201,
      g: 88,
      b: 3
    }
  }
  if (url.includes("amymd")) {
    return {
      r: 153,
      g: 36,
      b: 255
    }
  }
  if (url.includes("sia") || url.includes("adidas")) {
    return {

      r: 154,
      g: 0,
      b: 219
    }
  }
  return {

    r: 154,
    g: 0,
    b: 219
  }
}

const DashCalendar = ({ frequencies, events }: DashCalendarProps) => {

  const { r, g, b } = theme()


  const today = startOfToday();
  const [currentMonth] = useState(format(today, 'MMM-yyyy'));
  const firstDayCurrentMonth = parse(currentMonth, 'MMM-yyyy', new Date());
  const startDate = startOfWeek(firstDayCurrentMonth); // include previous days of the week
  const endDate = endOfWeek(endOfMonth(firstDayCurrentMonth)); // include next days of the week

  const days = eachDayOfInterval({
    start: startDate,
    end: endDate,
  });

  const filterEvents = (events: CalendarEvent[], date: Date): { currentDateEvents: CalendarEvent[], length: number, total: number } => {
    const targetDate = new Date(date);
    targetDate.setHours(0, 0, 0, 0);
    const filteredEvents = events.filter((event) => {
      const eventStartDate = new Date(event.startDate);
      const eventEndDate = new Date(event.endDate);
      eventStartDate.setHours(0, 0, 0, 0);
      eventEndDate.setHours(0, 0, 0, 0);
      return targetDate >= eventStartDate && targetDate <= eventEndDate;
    });

    if (filteredEvents.length > 5) {
      return { currentDateEvents: filteredEvents.slice(0, 4), length: filteredEvents.length - 5, total: filteredEvents.length }
    }
    return { currentDateEvents: filteredEvents, length: 0, total: filteredEvents.length }
  }

  const weeks = (date: Date): number => eachWeekOfInterval({ start: startOfMonth(date), end: endOfMonth(date) }).length;

  const getBgOpacity = (day: Date): { backgroundColor: string, color?: string } => {
    // If the day is not in the current month, return the fixed styling.
    if (!isSameMonth(day, firstDayCurrentMonth)) {
      return { backgroundColor: '#E5E7EB', color: '#94A3B8' };
    }
    // Otherwise, use your existing logic based on frequency.
    const formattedDate = format(day, 'yyyy-MM-dd');
    const matchedDate = frequencies.find((item) => item.date === formattedDate);
    return matchedDate
      ? { backgroundColor: `rgba(${r}, ${g}, ${b}, ${matchedDate.frequency / 10})`, color: (matchedDate.frequency / 10) > 0.5 ? "#fff" : "#000" }
      : { backgroundColor: '#f9fafb' }; // fallback color
  };


  const colStartClasses = [
    '',
    'col-start-2',
    'col-start-3',
    'col-start-4',
    'col-start-5',
    'col-start-6',
    'col-start-7',
  ];

  return (
    <>
      <div className='w-full h-full 2xl:ps-3'>
        <div className="grid grid-cols-7 2xl:py-4 place-items-center text-xs 2xl:text-sm leading-6 text-center text-gray-500">
          <div className='w-10'>S</div>
          <div className='w-10'>M</div>
          <div className='w-10'>T</div>
          <div className='w-10'>W</div>
          <div className='w-10'>T</div>
          <div className='w-10'>F</div>
          <div className='w-10'>S</div>
        </div>
        <div className="grid grid-cols-7 2xl:gap-y-4 gap-2 place-items-center mt-2 text-sm max-h-[90%] min-h-[85%]">
          {days.map((day, dayIdx) => {
            return (
              <div
                key={day.toString()}
                className={cn(
                  dayIdx === 0 && colStartClasses[getDay(day)],
                  'py-1.5 sm:size-12 aspect-square flex items-center justify-center'
                )}
              >
                <Popover>
                  <PopoverTrigger>
                    <div
                      className={cn(
                        isToday(day) && 'font-medium text-black',
                        !isToday(day) && isSameMonth(day, firstDayCurrentMonth) && 'text-gray-700',
                        !isToday(day) && !isSameMonth(day, firstDayCurrentMonth) && 'text-slate-400',
                        (weeks(new Date()) > 5) ? "w-7 sm:w-9 h-7 sm:h-9" : "w-10 sm:w-12 h-10 sm:h-12 ",
                        'mx-auto flex items-center justify-center rounded-full aspect-square',
                      )}
                      style={getBgOpacity(day)}
                    >
                      <time dateTime={format(day, 'yyyy-MM-dd')}>{format(day, 'd')}</time>
                    </div>
                  </PopoverTrigger>
                  <PopoverContent side="right">
                    <div className='flex flex-col gap-2'>
                      {filterEvents(events, day).currentDateEvents.map(event => {
                        return (
                          <div className='flex items-center justify-between' key={event.title}>
                            <span className={cn('w-1 h-4 rounded-full',
                              event.type === "Post" ? "bg-post-dark" : "bg-campaign-dark")}></span>
                            <p className='text-xs font-poppins'>{event.title.slice(0, 32)}...</p>
                          </div>
                        )
                      })}
                      {filterEvents(events, day).length > 0 &&
                        <p className='text-[0.6rem] font-poppins w-full text-end'>+more</p>}
                      {filterEvents(events, day).total === 0 && <div className='flex items-center justify-between'>
                        <span className='w-1 h-4 rounded-full bg-gray-200'></span>
                        <p className='text-xs font-poppins'>No scheduled post</p>
                      </div>}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            )
          })}
        </div>
      </div>
    </>
  );
};

export default DashCalendar;
